import dayjs, { type Dayjs } from 'dayjs';
import { parseShipmentDayjs } from './utils';

export type UserDateFormatPreference =
  'MM/DD/YYYY' | 'DD/MM/YYYY' | 'YYYY/MM/DD';

export type UserDateMonthStyle = 'name' | 'numeric' | 'full';

export const DEFAULT_USER_DATE_FORMAT: UserDateFormatPreference = 'MM/DD/YYYY';

const VALID_DATE_FORMATS: UserDateFormatPreference[] = [
  'MM/DD/YYYY',
  'DD/MM/YYYY',
  'YYYY/MM/DD',
];

export interface FormatUserDateTimeOptions {
  /** @default true */
  includeYear?: boolean;
  /** @default true */
  includeTime?: boolean;
  /** 'numeric' shows "08", 'full' shows "August" instead of "Aug". @default 'name' */
  monthStyle?: UserDateMonthStyle;
}

const buildTemplate = (
  preference: UserDateFormatPreference,
  {
    includeYear = true,
    includeTime = true,
    monthStyle = 'name',
  }: FormatUserDateTimeOptions
): string => {
  const monthToken =
    monthStyle === 'numeric' ? 'MM' : monthStyle === 'full' ? 'MMMM' : 'MMM';
  const dayToken = 'DD';
  const monthDaySeparator = monthStyle === 'numeric' ? '/' : ' ';

  const monthDay =
    preference === 'DD/MM/YYYY'
      ? `${dayToken}${monthDaySeparator}${monthToken}`
      : `${monthToken}${monthDaySeparator}${dayToken}`;

  const timeToken = includeTime ? 'h:mm A' : '';

  // Show year first only when included; otherwise use the standard date-first format.
  if (preference === 'YYYY/MM/DD' && includeYear) {
    return [`YYYY, ${monthDay}`, timeToken].filter(Boolean).join(' ');
  }

  const trailing = [includeYear ? 'YYYY' : '', timeToken]
    .filter(Boolean)
    .join(' ');
  return trailing ? `${monthDay}, ${trailing}` : monthDay;
};

//  Formats a timestamp using the user's preferred date format, with optional
//  year, time, and month style customization.

const toDayjs = (
  value?: string | number | Date | Dayjs | null,
  dateFormat?: string | null
): Dayjs | null => {
  if (value == null || value === '') return null;
  if (dayjs.isDayjs(value)) return value.isValid() ? value : null;
  if (value instanceof Date || typeof value === 'number') {
    const parsed = dayjs(value);
    return parsed.isValid() ? parsed : null;
  }
  return parseShipmentDayjs(String(value), dateFormat);
};

export const formatUserDateTime = (
  value?: string | number | Date | Dayjs | null,
  dateFormat?: string | null,
  options?: FormatUserDateTimeOptions
): string => {
  const parsed = toDayjs(value, dateFormat);
  if (!parsed) return '';

  const preference =
    dateFormat &&
    VALID_DATE_FORMATS.includes(dateFormat as UserDateFormatPreference)
      ? (dateFormat as UserDateFormatPreference)
      : DEFAULT_USER_DATE_FORMAT;

  return parsed.format(buildTemplate(preference, options ?? {}));
};

/**
 * Formats a date as a plain numeric string in the user's preferred slash
 * ordering (e.g. "03/21/2025" for MM/DD/YYYY, "21/03/2025" for DD/MM/YYYY).
 * For special-case UI (the Home date filter's pill label and calendar
 * inputs) that intentionally shows the raw numeric format rather than the
 * "MMM D, YYYY"-style used everywhere else.
 */
export const formatUserDateNumeric = (
  value?: string | number | Date | Dayjs | null,
  dateFormat?: string | null
): string => {
  const parsed = toDayjs(value, dateFormat);
  if (!parsed) return '';

  const preference =
    dateFormat &&
    VALID_DATE_FORMATS.includes(dateFormat as UserDateFormatPreference)
      ? (dateFormat as UserDateFormatPreference)
      : DEFAULT_USER_DATE_FORMAT;

  return parsed.format(preference);
};
